import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Info, X } from 'lucide-react';
import { reportsAPI } from '../../../services/api';
import { scrollContentToTop } from '../../../utils/scroll';
import { formatPatientAge } from '../../../utils/age';
import useReport from '../shared/useReport';
import KpiStrip from '../shared/KpiStrip';
import ChartPanel from '../shared/ChartPanel';
import ReportTable from '../shared/ReportTable';
import ReportPrintLayout from '../shared/ReportPrintLayout';
import PrintButton from '../shared/PrintButton';
import DateRangeFilter, { getPresetRange } from '../shared/DateRangeFilter';
import { PALETTE, axisProps, fmtNum, tooltipStyle } from '../shared/chartTheme';
import { DASH, StatusBadge, col, fmtDate, forPrint, human } from '../shared/profileKit';
import PatientProfileView from '../patientProfile/PatientProfileView';

// Frequent Patients: who came most often in a period, and what brings them back. A patient opens
// their Patient Profile on the same period, where the visits, prescriptions and medicines are.
//
// What a patient came with (diagnoses and complaints) arrives only for readers with the Clinical
// reports permission; the server leaves it out for anyone else, and this screen shows what arrived.

const MIN_CHOICES = [
  { value: 1, label: 'Everyone' },
  { value: 2, label: '2 or more' },
  { value: 3, label: '3 or more' },
  { value: 5, label: '5 or more' },
  { value: 10, label: '10 or more' },
];

/** "1 visit", "5 visits". */
const plural = (n, one, many = `${one}s`) => `${fmtNum(n)} ${Number(n) === 1 ? one : many}`;

/** Age the way the rest of the app writes it ("5 months", "about 6 years"), then gender. */
const ageGender = (r) => [formatPatientAge(r), r.gender ? human(r.gender) : null].filter(Boolean).join(', ') || DASH;

const gapText = (r) => {
  if (r.averageGapDays === null || r.averageGapDays === undefined) return DASH;
  const d = Number(r.averageGapDays);
  return d < 1 ? 'Same day' : `Every ${plural(Math.round(d), 'day')}`;
};

const flagsText = (r) =>
  [
    Number(r.emergencies) > 0 && plural(r.emergencies, 'emergency', 'emergencies'),
    Number(r.revisits) > 0 && plural(r.revisits, 'revisit'),
    Number(r.cancelled) > 0 && `${fmtNum(r.cancelled)} cancelled`,
  ]
    .filter(Boolean)
    .join(', ');

/** "diagnosed with" / "who complained of": the two lists can hold different people. */
const filterVerb = (f) => (f.kind === 'DIAGNOSIS' ? 'diagnosed with' : 'who complained of');
const filterPhrase = (f) => `${filterVerb(f)} ${f.reason}`;

/**
 * The reasons a row names: its three commonest, with the reason the list is narrowed to put first
 * when it is further down the patient's list, so a filtered row always shows why it is there.
 */
const reasonsShown = (top = [], all = [], match) => {
  const shown = match && all.includes(match) && !top.includes(match) ? [match, ...top.slice(0, 2)] : top;
  return { shown, more: Math.max(all.length - shown.length, 0) };
};

const reasonsText = (r, filter) => {
  const line = (label, top, all, kind) => {
    const { shown, more } = reasonsShown(top, all, filter?.kind === kind ? filter.reason : null);
    return shown.length ? `${label}: ${shown.join(', ')}${more ? ` and ${more} more` : ''}` : null;
  };
  return [
    line('Diagnosed', r.diagnoses, r.diagnosesAll, 'DIAGNOSIS'),
    line('Complained of', r.complaints, r.complaintsAll, 'COMPLAINT'),
  ]
    .filter(Boolean)
    .join('. ');
};

const ReasonLine = ({ top, all, match, className }) => {
  const { shown, more } = reasonsShown(top, all, match);
  if (!shown.length) return null;
  return (
    <div className={className}>
      {shown.map((reason, i) => (
        <React.Fragment key={reason}>
          {i > 0 && ', '}
          <span className={reason === match ? 'font-semibold text-emerald-700' : undefined}>{reason}</span>
        </React.Fragment>
      ))}
      {more > 0 && <span className="text-gray-400"> and {more} more</span>}
    </div>
  );
};

// Seven columns so the reasons have room to read: age and gender sit under the name, and what
// the visits included (emergencies, revisits, cancellations) under the visit count. The CSV keeps
// the key figures as plain numbers and dates, so a spreadsheet can sort and add them.
const patientColumns = (clinical, filter) => [
  col('patientName', 'Patient', 'text', {
    text: (r) =>
      [
        `${r.patientName || DASH} (${r.mrn})`,
        ageGender(r),
        r.patientStatus && r.patientStatus !== 'ACTIVE' ? human(r.patientStatus) : null,
        r.archived ? 'archived' : null,
      ]
        .filter(Boolean)
        .join(', '),
    render: (r) => (
      <div className="whitespace-nowrap">
        <div className="font-medium text-gray-900 flex items-center gap-2">
          {r.patientName || DASH}
          {r.patientStatus && r.patientStatus !== 'ACTIVE' && <StatusBadge value={r.patientStatus} />}
          {r.archived && <span className="text-xs font-normal text-amber-700">Archived</span>}
        </div>
        <div className="text-xs text-gray-500">
          <span className="font-mono">{r.mrn}</span> · {ageGender(r)}
        </div>
      </div>
    ),
  }),
  col('phone', 'Phone', 'mono'),
  col('visits', 'Visits', 'number', {
    text: (r) => [`${r.visits} (${r.allTimeVisits} all time)`, flagsText(r)].filter(Boolean).join(', '),
    csv: (r) => r.visits,
    render: (r) => (
      <div className="whitespace-nowrap">
        <div className="font-semibold text-gray-900">{fmtNum(r.visits)}</div>
        <div className="text-xs text-gray-500">{fmtNum(r.allTimeVisits)} all time</div>
        {flagsText(r) && <div className="text-xs text-amber-700">{flagsText(r)}</div>}
      </div>
    ),
  }),
  col('averageGapDays', 'Comes Back', 'number', {
    text: gapText,
    csv: (r) => (r.averageGapDays === null || r.averageGapDays === undefined ? '' : r.averageGapDays),
  }),
  col('lastVisit', 'Seen', 'date', {
    text: (r) => `first ${fmtDate(r.firstVisit)}, last ${fmtDate(r.lastVisit)}`,
    csv: (r) => r.lastVisit || '',
    render: (r) => (
      <div className="whitespace-nowrap">
        <div>Last {fmtDate(r.lastVisit)}</div>
        <div className="text-xs text-gray-500">First {fmtDate(r.firstVisit)}</div>
      </div>
    ),
  }),
  col('mainDoctor', 'Mostly Seen By', 'text', {
    text: (r) => `${r.mainDoctor || DASH}${Number(r.doctors) > 1 ? ` (${r.doctors} doctors)` : ''}`,
    render: (r) => (
      <div className="whitespace-nowrap">
        <div>{r.mainDoctor || DASH}</div>
        {Number(r.doctors) > 1 && <div className="text-xs text-gray-500">{fmtNum(r.doctors)} doctors in all</div>}
      </div>
    ),
  }),
  ...(clinical
    ? [
        col((r) => reasonsText(r, filter), 'What They Come With', 'wrap', {
          text: (r) => reasonsText(r, filter) || DASH,
          render: (r) =>
            (r.diagnosesAll || []).length || (r.complaintsAll || []).length ? (
              <div className="text-sm">
                <ReasonLine
                  top={r.diagnoses}
                  all={r.diagnosesAll}
                  match={filter?.kind === 'DIAGNOSIS' ? filter.reason : null}
                  className="text-gray-900"
                />
                <ReasonLine
                  top={r.complaints}
                  all={r.complaintsAll}
                  match={filter?.kind === 'COMPLAINT' ? filter.reason : null}
                  className="text-xs text-gray-500"
                />
              </div>
            ) : (
              DASH
            ),
        }),
      ]
    : []),
];

const reasonColumns = (label) => [
  col('reason', label, 'wrap'),
  col('patients', 'Patients', 'number'),
  col('prescriptions', 'Prescriptions', 'number'),
];

const revisitColumns = [
  col('reason', 'Reason Given', 'wrap'),
  col('patients', 'Patients', 'number'),
  col('visits', 'Visits', 'number'),
];

const distributionColumns = [
  col('label', 'Visits in the Period', 'text'),
  col('patients', 'Patients', 'number'),
  col('visits', 'Visits', 'number'),
];

const Note = ({ children, onClear }) => (
  <div className="border-l-4 border-sky-500 bg-sky-50 text-sky-900 p-3 rounded-lg text-sm flex items-start gap-2">
    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
    <div className="flex-1">{children}</div>
    {onClear && (
      <button
        type="button"
        onClick={onClear}
        className="inline-flex items-center gap-1 text-sm font-medium text-sky-800 hover:text-sky-900 whitespace-nowrap"
      >
        <X className="w-4 h-4" /> Show all listed patients
      </button>
    )}
  </div>
);

const DistributionTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const b = payload[0].payload;
  return (
    <div style={tooltipStyle.contentStyle} className="px-3 py-2">
      <div className="text-xs text-gray-500">{b.label === '1' ? 'Came once' : `Came ${b.label} times`}</div>
      <div className="text-sm font-semibold text-gray-900">{plural(b.patients, 'patient')}</div>
      <div className="text-xs text-gray-600">{plural(b.visits, 'visit')} between them</div>
    </div>
  );
};

const FrequentPatientsReport = () => {
  const [range, setRange] = useState(() => getPresetRange('last6Months'));
  const [preset, setPreset] = useState('last6Months');
  const [minVisits, setMinVisits] = useState(2);
  const [reasonFilter, setReasonFilter] = useState(null);
  const [openId, setOpenId] = useState(null);
  const listRef = useRef(null);
  const wasOpen = useRef(false);

  const fetcher = useCallback(
    () => reportsAPI.getFrequentPatients({ from: range.from, to: range.to, minVisits }),
    [range, minVisits]
  );
  const { data, loading } = useReport(fetcher);

  // The period the rows actually cover: the server fills a cleared From or To, and while a newer
  // load is on its way the rows on screen still belong to the last one.
  const shownRange = data?.range || range;

  // A new period or minimum is a new list: a filter picked from the old one no longer applies.
  useEffect(() => { setReasonFilter(null); }, [range, minVisits]);
  // Opening a patient starts at the top of their profile; coming back returns to the list the
  // patient was picked from, not to the top of the report above it.
  useEffect(() => {
    if (openId) scrollContentToTop();
    else if (wasOpen.current) listRef.current?.scrollIntoView({ block: 'start' });
    wasOpen.current = Boolean(openId);
  }, [openId]);

  const clinical = Boolean(data?.access?.clinical);
  const k = data?.kpis || {};
  const rows = useMemo(() => data?.rows || [], [data]);
  const shown = useMemo(() => {
    if (!reasonFilter) return rows;
    const key = reasonFilter.kind === 'DIAGNOSIS' ? 'diagnosesAll' : 'complaintsAll';
    return rows.filter((r) => (r[key] || []).includes(reasonFilter.reason));
  }, [rows, reasonFilter]);
  const cols = useMemo(() => patientColumns(clinical, reasonFilter), [clinical, reasonFilter]);
  const bars = useMemo(
    () => (data?.distribution || []).map((b) => ({ ...b, patients: Number(b.patients), visits: Number(b.visits) })),
    [data]
  );
  const diagnoses = data?.reasons?.diagnoses || [];
  const complaints = data?.reasons?.complaints || [];
  const revisitReasons = data?.revisitReasons || [];
  const listedNoun = minVisits === 1 ? 'patients seen' : `patients with ${minVisits} or more visits`;
  const listedLabel = minVisits === 1 ? 'every patient seen' : listedNoun;
  // A reason's own table counts every listed patient who had it; when the list is capped, some of
  // them are past the cap and cannot appear below.
  const partOfReason = Boolean(reasonFilter && data?.truncated && reasonFilter.total > shown.length);

  const pickReason = (kind) => (r) => {
    setReasonFilter({ kind, reason: r.reason, total: Number(r.patients) });
    listRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  const kpis = [
    { label: 'Patients Seen', value: fmtNum(k.patients ?? 0), sub: `${plural(k.visits ?? 0, 'visit')} in the period` },
    {
      label: 'Came Back',
      value: fmtNum(k.returningPatients ?? 0),
      sub:
        k.returningVisitShare !== null && k.returningVisitShare !== undefined
          ? `2 or more visits, making ${k.returningVisitShare}% of all visits`
          : '2 or more visits',
    },
    { label: 'Visits per Patient', value: k.averageVisits ?? DASH, sub: 'On average' },
    { label: 'Most Visits', value: fmtNum(k.mostVisits ?? 0), sub: 'By one patient' },
    { label: 'Listed', value: fmtNum(k.qualifyingPatients ?? 0), sub: minVisits === 1 ? 'Everyone seen' : `${minVisits} or more visits` },
  ];

  let listTitle = `${plural(shown.length, 'patient')}${data?.truncated ? ` (the first ${fmtNum(data.listLimit)})` : ''}`;
  if (reasonFilter) {
    listTitle = partOfReason
      ? `${fmtNum(shown.length)} of ${plural(reasonFilter.total, 'patient')} ${filterPhrase(reasonFilter)}`
      : `${plural(shown.length, 'patient')} ${filterPhrase(reasonFilter)}`;
  }

  const printTables = [
    { title: 'Patients', columns: forPrint(cols), rows: shown },
    ...(diagnoses.length ? [{ title: 'Diagnoses they came with', columns: forPrint(reasonColumns('Diagnosis')), rows: diagnoses }] : []),
    ...(complaints.length ? [{ title: 'Complaints they came with', columns: forPrint(reasonColumns('Complaint')), rows: complaints }] : []),
    ...(revisitReasons.length ? [{ title: 'Reasons given at the counter for a revisit', columns: forPrint(revisitColumns), rows: revisitReasons }] : []),
    { title: 'Patients by number of visits', columns: forPrint(distributionColumns), rows: bars },
  ];

  return (
    <>
      <div hidden={Boolean(openId)} className="space-y-5">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[300px]">
            <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
          </div>
          <div className="pt-4"><PrintButton /></div>
        </div>

        <div className="bg-white rounded-2xl shadow p-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700 mr-1">List patients with</span>
          {MIN_CHOICES.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setMinVisits(c.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                minVisits === c.value ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {c.label}
            </button>
          ))}
          <span className="text-sm text-gray-500">visits in the period</span>
        </div>

        <KpiStrip items={kpis} loading={loading && !data} />

        <ChartPanel title="Patients by number of visits">
          {loading && !data ? (
            <p className="text-gray-400 text-sm text-center py-16">Loading</p>
          ) : !bars.some((b) => b.patients > 0) ? (
            <p className="text-gray-400 text-sm text-center py-16">No visits in the selected range</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={bars} margin={{ left: 0, right: 16, top: 8 }}>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
                  <XAxis dataKey="label" {...axisProps} />
                  <YAxis {...axisProps} allowDecimals={false} width={48} />
                  <Tooltip content={<DistributionTooltip />} cursor={{ fill: '#f3f4f6' }} />
                  <Bar dataKey="patients" radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}>
                    {bars.map((b) => (
                      <Cell key={b.label} fill={b.from >= minVisits ? PALETTE[0] : '#d1d5db'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs text-gray-500 mt-2">
                Visits each patient made in the period. The green bars are the patients listed below; grey bars fall under the minimum.
              </p>
            </>
          )}
        </ChartPanel>

        {clinical ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ReportTable
              title="Diagnoses they come with"
              columns={reasonColumns('Diagnosis')}
              rows={diagnoses}
              searchable={false}
              onRowClick={pickReason('DIAGNOSIS')}
              emptyText="No diagnosis recorded on their visits"
              footnote={`Across the ${listedNoun}, most patients first. Click one to list only those patients.`}
            />
            <ReportTable
              title="Complaints they come with"
              columns={reasonColumns('Complaint')}
              rows={complaints}
              searchable={false}
              onRowClick={pickReason('COMPLAINT')}
              emptyText="No complaint recorded on their visits"
              footnote="The chief complaints the doctor recorded. Click one to list only those patients."
            />
          </div>
        ) : (
          data && (
            <Note>What patients come with (their diagnoses and complaints) is shown to users with the Clinical reports permission.</Note>
          )
        )}

        {revisitReasons.length > 0 && (
          <ReportTable
            title="Reasons given at the counter for a revisit"
            columns={revisitColumns}
            rows={revisitReasons}
            searchable={false}
            footnote="What reception wrote when a token was marked as a revisit."
          />
        )}

        <div ref={listRef} className="space-y-3 scroll-mt-4">
          {data?.truncated && !reasonFilter && (
            <Note>
              Showing the first {fmtNum(data.listLimit)} patients by number of visits. Raise the minimum or shorten the period to see the rest.
            </Note>
          )}
          {reasonFilter && (
            <Note onClear={() => setReasonFilter(null)}>
              {partOfReason ? (
                <>
                  {fmtNum(shown.length)} of the {plural(reasonFilter.total, 'patient')} {filterVerb(reasonFilter)}{' '}
                  <span className="font-semibold">{reasonFilter.reason}</span> are among the first {fmtNum(data.listLimit)} listed. Raise
                  the minimum or shorten the period to see the rest.
                </>
              ) : (
                <>
                  Showing the {plural(shown.length, 'patient')} {filterVerb(reasonFilter)}{' '}
                  <span className="font-semibold">{reasonFilter.reason}</span> on a visit in this period.
                </>
              )}
            </Note>
          )}
          <ReportTable
            key={reasonFilter ? `${reasonFilter.kind}:${reasonFilter.reason}` : 'all'}
            title={listTitle}
            columns={cols}
            rows={shown}
            onRowClick={(r) => setOpenId(r.patientId)}
            csvName={`frequent-patients-${shownRange.from}-to-${shownRange.to}`}
            emptyText={loading ? 'Loading' : `No ${listedNoun} in the selected range`}
            footnote={`Most visits first. A visit is a token that was not cancelled. Click a patient to open their profile for ${fmtDate(shownRange.from)} to ${fmtDate(shownRange.to)}, with every visit, prescription and medicine.`}
          />
        </div>

        <ReportPrintLayout
          title="Frequent Patients"
          subtitle={`Period: ${fmtDate(shownRange.from)} to ${fmtDate(shownRange.to)}. Listed: ${listedLabel}${reasonFilter ? `, ${filterPhrase(reasonFilter)}` : ''}.`}
          kpis={kpis.map((x) => ({ label: x.label, value: x.value }))}
          tables={printTables}
        />
      </div>

      {openId && (
        <PatientProfileView
          key={`${openId}-${shownRange.from}-${shownRange.to}`}
          patientId={openId}
          initialPeriod={{ from: shownRange.from, to: shownRange.to }}
          backLabel="Back to frequent patients"
          onBack={() => setOpenId(null)}
          onOpenPatient={(id) => setOpenId(id)}
        />
      )}
    </>
  );
};

export default FrequentPatientsReport;
